import React, { FC, useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { ActionsPopup, FullPageError } from 'src/components';
import DevTools from 'src/apps/Tools/DevTools';
import { ROUTES } from 'src/constants/routes.const';
import { useUserContext } from 'src/context/user';
import useSafePush from 'src/hooks/useSafePush';
import { PremiumConfigProps, usePremiumConfig } from 'src/hooks/usePremiumConfig';
import { RestrictToAuth } from 'src/utilityComponents';
import { PremiumPrice } from 'src/api/internal/schema';
import { UpsellPageHeader } from '../UpsellPageHeader';
import { SubscribeHandlerConfig, useSubscribeHandler } from '../hooks/useSubscribeHandler';
import { usePageTemplate } from '../hooks/usePageTemplate';
import { getUpsellPageErrorInfo } from '../utils/getUpsellPageErrorInfo';
import { BenefitsSection } from '../BenefitsSection';
import { FAQSection } from '../FAQSection';
import { PricesSection } from '../PricesSection';
import { PricesModal } from '../PricesModal';
import { ReviewSection } from '../ReviewSection';
import { FooterButtons } from '../FooterButtons';
import * as S from './styles';

export const msgs = defineMessages({
  alreadyPro: { id: 'web_plans_proalready_1' },
  errorCode: { id: 'web_chargebee_error_code' },
  backToHome: { id: 'ea_plans_back' },
  backToPlanPage: { id: 'learning_session_error_button_plans' },
});

export const UpsellPage: FC<{
  premiumConfigProps?: PremiumConfigProps;
  subscribeHandlerConfig?: SubscribeHandlerConfig;
  secondaryCta?: string;
  onClose: () => void;
  onLoadError?: () => void;
}> = ({ premiumConfigProps, subscribeHandlerConfig, secondaryCta, onClose, onLoadError }) => {
  const intl = useIntl();
  const { safePush } = useSafePush();
  const { user } = useUserContext();

  const [selectedPrice, setSelectedPrice] = useState<PremiumPrice | null>(null);
  const [countryOverride, setCountryOverride] = useState<string | undefined>(undefined);
  const [showPricesModal, setShowPricesModal] = useState(false);

  const secondaryAction = useMemo(
    () =>
      secondaryCta
        ? {
            text: secondaryCta,
            onClick: onClose,
          }
        : undefined,
    [secondaryCta, onClose],
  );

  const { error: premiumConfigError, premiumConfig } = usePremiumConfig({
    countryOverride,
    ...premiumConfigProps,
  });

  const pageTemplate = usePageTemplate(premiumConfig);

  const {
    isWaitingForAuth,
    isCreatingSession,
    error: subscribeError,
    subscribeHandler,
  } = useSubscribeHandler(premiumConfig, selectedPrice, subscribeHandlerConfig);

  const { fullPageError, showAlreadyProModal } = getUpsellPageErrorInfo({
    user,
    premiumConfig,
    premiumConfigError,
    subscribeError,
  });

  useEffect(() => {
    if (premiumConfigError) {
      console.error(`Could not display upsell page: failed to load premium config.`);

      if (onLoadError) {
        onLoadError();
      }
    }
  }, [premiumConfigError, onLoadError]);

  if (fullPageError) {
    const { descriptionMsg, code } = fullPageError;
    return (
      <FullPageError
        buttonHref={ROUTES.payment.plans}
        buttonText={intl.formatMessage(msgs.backToPlanPage)}
        detail={intl.formatMessage(descriptionMsg)}
      >
        {code && (
          <div>
            {intl.formatMessage(msgs.errorCode)}: {code}
          </div>
        )}
      </FullPageError>
    );
  }

  if (!premiumConfig || !pageTemplate || isCreatingSession) {
    return <S.Loader />;
  }

  const Wrapper = isWaitingForAuth ? RestrictToAuth : React.Fragment;
  return (
    <Wrapper>
      <DevTools page="plans" setCountry={setCountryOverride} />

      <ActionsPopup
        header={intl.formatMessage(msgs.alreadyPro)}
        primaryBtnText={intl.formatMessage(msgs.backToHome)}
        primaryHref={ROUTES.dashboard}
        isOpen={showAlreadyProModal}
        onClose={() => safePush(ROUTES.dashboard)}
      />

      <PricesModal
        isOpen={showPricesModal}
        handleClose={() => setShowPricesModal(false)}
        prices={premiumConfig.prices}
        selectedPrice={selectedPrice}
        setSelectedPrice={setSelectedPrice}
        onSubscribe={subscribeHandler}
      />

      <UpsellPageHeader pageTemplate={pageTemplate} onClose={onClose} />

      <S.PageContent>
        <BenefitsSection pageTemplate={pageTemplate} />

        <PricesSection
          prices={premiumConfig.prices}
          selectedPrice={selectedPrice}
          setSelectedPrice={setSelectedPrice}
          onSubscribe={subscribeHandler}
          secondaryAction={secondaryAction}
        />

        <ReviewSection />

        <FAQSection />
      </S.PageContent>

      <S.StickyFooter>
        <FooterButtons
          onViewPrices={() => setShowPricesModal(true)}
          secondaryAction={secondaryAction}
        />
      </S.StickyFooter>
    </Wrapper>
  );
};
